import { combineLatest, Observable, Subject, Subscription } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { AuthService } from '../../services/auth.service';
import { Menu } from '../../models/menu';
import { ActivatedRoute, Router } from '@angular/router';
import { TenantStateService } from 'src/app/shared/services/tenant.state.service';
import { TenantState } from 'src/app/store/state/tenant.state';
import { TenantSessionState } from 'src/app/store/state/tenant-session.state';
import { Select } from '@ngxs/store';
import { Tenant } from 'src/app/models/tenant';
import { Module } from 'src/app/models/module';
import { FeatureSettings, TenantFeatures, awaitTenantFeatures, isFeatureEnabled } from 'src/app/models/featureSettings';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { UserService } from 'src/app/users/user.service';
import { AppConfigurationService, AppSettings } from 'src/app/shared/services/app-configuration.service';
import { cloneDeep } from 'lodash';
import { ChildModule } from 'src/app/models/childModule';
import { TenantUser } from 'src/app/models/tenantUser';
import { checkModuleGroupPermissions, findRouteData } from 'src/app/shared/handler/common-functions.util';
import { DrawerService } from 'src/app/shared/drawer/drawer.service';
import { RouterModuleResolverService } from 'src/app/shared/services/router-module-resolver.service';
import { NavDrawerComponent } from '../nav-drawer/nav-drawer.component';
import { SpaRoutingService, Modules, isModule } from 'src/app/shared/services/spa.routing.service';
import { ServiceCenterStateService } from 'src/app/service-center/service-center-state.service';
import { IssuesStateService } from 'src/app/issues/issues-state.service';
import { IssueSyncService } from 'src/app/issues/sync/issue-sync.service';
import { ThirdPartyApp, enabledThirdPartyApps, thirdPartyAppByFeature } from 'src/app/links/links-registry';
import { ThirdPartyAppLauncherService } from 'src/app/links/third-party-app-launcher.service';
import { TranslateService } from '@ngx-translate/core';


@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.css'],
    standalone: false
})

export class HeaderComponent implements OnInit, OnDestroy {
  @Select(TenantState.getCurrentTenant) getCurrentTenant$: Observable<Tenant>;
  @Select(TenantSessionState.getTenantModules) getTenantModules$: Observable<Module[]>;
  @SelectSnapshot(TenantState.getTenants) getTenants: Tenant[];
  @SelectSnapshot(TenantState.getCurrentModuleSnap) getCurrentModuleSnap: Module;
  @Select(TenantState.getCurrentModule) getCurrentModule$: Observable<Module>;
  @SelectSnapshot(TenantState.getLanguage) getLanguage: string;
  @Select(TenantSessionState.getCurrentUser) getCurrentUser$: Observable<TenantUser>;
  @Select(TenantSessionState.getTenantFeatures) getTenantFeatures$: Observable<FeatureSettings>;

  public tenantName: string;
  public name: string;
  public menuItems$: Observable<Menu[]>;
  public menuItems: Menu[];
  public hasViewDashboard: boolean;
  public hasViewPart: boolean;
  public hasViewDiagnostics: boolean;
  public isLoggedIn: boolean;
  public logoUrl: string;
  public subscription: Subscription;
  public tenantId: string;
  public moduleSubscription: Subscription;
  public tenant: Tenant;
  public language = '';
  public userLanguage = '';
  headerItems: Module[];
  currentModule: Module;
  configurationModule: Module;
  profileModule: Module;
  notificationModule: ChildModule;
  isAuthenticated = false;
  currentChildModule: any;
  destroy$: Subject<unknown> = new Subject();
  drawerToggled: boolean = false;
  modules: Module[];
  issuesUiEnabled = false;
  thirdPartyApps: ThirdPartyApp[] = [];
  tenantSwitching = false;
  constructor(
    private authService: AuthService,
    private router: Router,
    private tenantStateService: TenantStateService,
    private readonly appConfigurationService: AppConfigurationService,
    private userService: UserService,
    private drawerService: DrawerService,
    private routerModuleResolver: RouterModuleResolverService,
    private spaRoutingService: SpaRoutingService,
    private serviceCenterStateService: ServiceCenterStateService,
    private issuesStateService: IssuesStateService,
    private issueSyncService: IssueSyncService,
    private appLauncher: ThirdPartyAppLauncherService,
    private activatedRoute: ActivatedRoute,
    private translate: TranslateService) {
  }

  selectedLanguage: string;
  gettingAlertType = false;

  ngOnInit(): void {
    this.selectedLanguage = this.getLanguage;
    // Nothing else activates the stored language before first render; the picker is lazy inside the profile menu.
    this.translate.use(this.selectedLanguage);
    this.appConfigurationService.settings$
      .pipe(take(1))
      .subscribe((settings: AppSettings) => {
        this.logoUrl = this.appConfigurationService.settings.contentDeliveryNetworkUrl + '/logo-full.png';
      });

    // Hide the header entirely until the user has an authenticated session.
    // Covers registration, auth-callback, silent-refresh, unauthorized, etc.
    // The header is on every route, so the tenant fetch waits for a user too:
    // a signed-out visitor's tenantdetail call can only 401. The user loads
    // asynchronously, so this cannot be a synchronous check in ngOnInit.
    this.authService.user$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.isAuthenticated = user != null;
        if (user != null && !this.tenantStateService.storeExists()) {
          this.tenantStateService.ensureHealthyState();
        }
      });

    combineLatest([this.getCurrentTenant$, this.getCurrentModule$, this.getCurrentUser$, this.getTenantModules$, this.getTenantFeatures$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([tenant, currentModule, currentUser, modules, features]) => {
        this.issuesUiEnabled = isFeatureEnabled(features, TenantFeatures.IssuesUI);
        this.thirdPartyApps = enabledThirdPartyApps(features);
        if (tenant && modules) {
          this.tenant = tenant;
          this.modules = modules;
          this.tenantId = this.tenant.externalId;
          this.tenantName = this.tenant.name;
          this.profileModule = this.modules.find(x => isModule(x, Modules.PROFILE));
          this.notificationModule = this.profileModule?.childModules.find(p => p.link === "notificationPreferences");
        }
        // Not gated on currentModule: a third-party app route resolves to no module, and the module nav
        // still belongs on screen there — just with nothing highlighted.
        this.currentModule = currentModule;
        if (this.modules?.length) {
          this.populateHeaderItems(currentUser);
          this.getSelectedChildModule();
        }
      });
  }

  openNotificationPreferences(): void {
    this.spaRoutingService.navigateToOwnNotificationPreferences();
  }

  getSelectedChildModule(): void {
    const params = this.router.url.split('/');
    let childModule = null;
    if (this.currentModule) {
      params.forEach(param => {
        this.currentModule.childModules?.forEach(cm => {
          // if child module has a link parameter, match against parameters (dashboard)
          if (cm.linkParameter
            && cm.linkParameter === param) {
            childModule = cm;
          }
          // if child module link equals one of the parameters
          else if (!cm.linkParameter
            && cm.link === param
            && cm.class !== 'hide') {
            childModule = cm;
          }
        });
      });
      // if a child module was found, set to selected module
      if (childModule) {
        this.currentChildModule = childModule;
      }
      else {
        this.currentChildModule = this.currentModule.childModules?.find(c => c.class !== 'hide');
      }
    }
  }
  toggleNavDrawer(): void {
    this.drawerToggled = !this.drawerToggled;
    if (this.drawerToggled) {
      const navDrawer = this.drawerService.open(
        NavDrawerComponent,
        [
          { input: 'currentModule', value: this.currentModule },
          { input: 'currentChildModule', value: this.currentChildModule },
          { input: 'headerItems', value: this.headerItems },
          { input: 'configModule', value: this.configurationModule },
          { input: 'profileModule', value: this.profileModule },
          { input: 'notificationModule', value: this.notificationModule },
          { input: 'thirdPartyApps', value: this.thirdPartyApps },
          { input: 'tenant', value: this.tenant },
          { input: 'accessibleTenants', value: this.accessibleTenants }
        ],
        {
          resizable: false,
        });

      // Covers every close path, including the drawer navigating on its own.
      this.drawerService.closed$.pipe(
        take(1))
        .subscribe(() => this.drawerToggled = false);
      navDrawer.navigate$.pipe(
        take(1))
        .subscribe(([selectedItem, module]) => {
          //close
          this.drawerService.close();
          this.drawerToggled = false;
          //do navigation
          this.processChildClick(selectedItem, module);
        });
      navDrawer.logout$.pipe(
        takeUntil(this.drawerService.closed$))
        .subscribe(($event: Event) => {
          this.logout($event);
        })
      navDrawer.switchTenant$.pipe(
        take(1))
        .subscribe((tenant: Tenant) => {
          this.drawerService.close();
          this.drawerToggled = false;
          this.switchTenant(tenant);
        });
    }
    else {
      this.drawerService.close();
      this.drawerToggled = false;
    }

  }
  populateHeaderItems(currentUser: TenantUser): void {
    const modules = cloneDeep(this.modules) as Module[];
    const profile = this.authService.getClaims();
    if (profile && this.authService.isLoggedIn()) {
      const accessibleModules = modules.filter(module => {
        return module.ordinal >= 0 //remove modules with ordinal less than 0
          // The Issues module is offered only where the screens are turned on. Its own
          // shell redirects a tenant without the flag, so an entry that led there would
          // take the reader to the landing page and look broken.
          && (module.parentRoutePath !== '/issues' || this.issuesUiEnabled)
          && module.rights.filter(y => profile.right.includes(y)).length > 0 //remove modules if user does not have the rights
          && checkModuleGroupPermissions(currentUser?.groups ?? [], module?.groups ?? []) //remove modules if user is not in the group;
      });
      this.configurationModule = accessibleModules.find(m => isModule(m, Modules.CONFIGURATION));
      this.headerItems = accessibleModules.filter(m => !isModule(m, Modules.CONFIGURATION));
      this.userLanguage = profile.language;
    }
  }

  logout(event): void {
    event.preventDefault();
    this.tenantStateService.clearTenantState();
    // Without this a different user on the same browser would inherit the prior
    // user's view state and cached records. Same logout-cleanup pattern as
    // clearTenantState above.
    this.serviceCenterStateService.clearAll();
    this.serviceCenterStateService.clearViewState();
    this.issuesStateService.clearAll();
    // The Issue replica outlives a sign-out otherwise. It is stored per tenant rather than
    // per user, so the next person to sign in on this browser would be handed the rows the
    // last one held. Awaited: the sign-out redirect would otherwise abort the delete
    // part-way, which is the case this exists to prevent.
    void this.issueSyncService.purge()
      .then(() => this.finishLogout(), () => this.finishLogout());
  }

  // The rest of the sign-out, once the Issue databases are gone. A failed purge still
  // signs the user out: leaving them logged in would be a worse answer to a delete that
  // did not finish.
  private finishLogout(): void {
    this.isLoggedIn = false;
    this.userService.logout().subscribe({
      next: () => this.authService.logout(),
      error: () => this.authService.logout()
    });
  }

  toggleClassAlink($event, selectedItem: any): void {
    $event.preventDefault();
    // Configuration's children are all hidden relocated entries; no visible child = module root.
    const firstChild = selectedItem.childModules?.find((c: ChildModule) => c.class !== 'hide');
    this.spaRoutingService.navigateToModule(selectedItem, firstChild);
  }

  async processChildClick(selectedItem: any, parentItem: Module): Promise<void> {
    this.currentChildModule = selectedItem;
    this.spaRoutingService.navigateToModule(parentItem, selectedItem);
  }

  get accessibleTenants(): Tenant[] {
    return this.getTenants ?? [];
  }

  // Third-party app routes were dropped from the menu config, so on those routes currentModule still reports
  // wherever the user navigated from. The route wins, and every module-derived highlight goes quiet.
  private get activeThirdPartyApp(): ThirdPartyApp | null {
    return thirdPartyAppByFeature(findRouteData<TenantFeatures>(this.activatedRoute.snapshot, 'feature')) ?? null;
  }

  private get highlightedModule(): Module | null {
    return this.activeThirdPartyApp ? null : this.currentModule;
  }

  isModuleActive(item: Module): boolean {
    return !!this.highlightedModule && item.name === this.highlightedModule.name;
  }

  get configurationActive(): boolean {
    return isModule(this.highlightedModule, Modules.CONFIGURATION);
  }

  get profileActive(): boolean {
    return isModule(this.highlightedModule, Modules.PROFILE);
  }

  get linksActive(): boolean {
    return this.activeThirdPartyApp != null;
  }

  launchThirdPartyApp(app: ThirdPartyApp): void {
    this.appLauncher.launch(app);
  }

  // setCurrentTenant already awaited the new tenant's modules; this bounded wait is only so the
  // feature-gated headerItems (issuesUiEnabled) are current before the target lookup.
  async switchTenant(tenant: Tenant): Promise<void> {
    if (tenant.externalId === this.tenantId) {
      return;
    }
    const modulePath = this.routerModuleResolver.resolveFromUrl(this.router.url).module?.parentRoutePath;
    // Raw segment, not a resolved childModule: it carries scopes the menu config doesn't (SC assets/locations, config section paths).
    const childSegment = this.router.url.split('?')[0].split('/').filter(Boolean)[2];
    this.tenantSwitching = true;
    try {
      await this.changeCurrentTenant(tenant.externalId);
    }
    catch (err) {
      this.tenantSwitching = false;
      throw err;
    }
    awaitTenantFeatures(this.getTenantFeatures$)
      .pipe(takeUntil(this.destroy$))
      .subscribe(async () => {
        try {
          const target = [...(this.headerItems ?? []), this.configurationModule]
            .filter(Boolean)
            .find(m => m.parentRoutePath === modulePath);
          await (target
            ? this.spaRoutingService.navigateToModuleHome(target, childSegment, tenant.externalId)
            : this.spaRoutingService.navigateToOperationalHome(tenant.externalId));
        }
        finally {
          this.tenantSwitching = false;
        }
      });
  }

  async changeCurrentTenant(newTenantId: string): Promise<void> {
    this.serviceCenterStateService.clearAll();
    this.issuesStateService.clearAll();
    await this.tenantStateService.setCurrentTenant(newTenantId);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

}
